import _ from 'lodash'

/**
 * Data diff utility - compares values with protects and ignores
 * Based on PHP dataDiff function
 */
export const dataDiff = (update, current, protects = [], ignores = [], parent = '') => {
  const _protects = protects instanceof Set ? protects : new Set(protects)
  const _ignores = ignores instanceof Set ? ignores : new Set(ignores)
  const result = {}

  if (_isScalar(update)) {
    if (!_isScalar(current) || !_weakEqual(update, current)) return update
    return undefined
  }

  for (const key of Object.keys(update)) {
    const value = update[key]
    const fullPath = parent ? `${parent}.${key}` : key

    // 1. Undefined check or Ignore check - check before accessing update[key]
    if (value === undefined || (_ignores.size > 0 && _ignores.has(fullPath))) {
      continue
    }

    // 2. Protect check
    if (_protects.size > 0 && _protects.has(fullPath)) {
      result[key] = value
      continue
    }

    if (Object.prototype.hasOwnProperty.call(current, key)) {
      const currentValue = current[key]

      if (_isScalar(value)) {
        // If types are different or weak comparison fails, assign to result
        if (!_isScalar(currentValue) || !_weakEqual(value, currentValue)) {
          const diff = dataDiff(value, currentValue, _protects, _ignores, fullPath)
          if (diff !== undefined) result[key] = diff
        }
      } else if (_isArrayOrObject(value)) {
        if (!_isArrayOrObject(currentValue)) {
          // Type changed from scalar to object/array
          result[key] = value
        } else if (Array.isArray(value) !== Array.isArray(currentValue)) {
          // One is array, other is object: full replacement
          result[key] = value
        } else if (Array.isArray(value) && Array.isArray(currentValue)) {
          // Array comparison: set difference (New - Old), ignoring position
          const added = value.filter((v) => !currentValue.some((c) => _.isEqual(v, c)))
          const removed = currentValue.filter((c) => !value.some((v) => _.isEqual(c, v)))

          if (added.length > 0 || removed.length > 0) {
            result[key] = added
          }
        } else {
          // Recursive diff for objects
          const isValueEmpty = _isEmpty(value)
          const isCurrentEmpty = _isEmpty(currentValue)
          const diff = dataDiff(Array.isArray(value) ? value : _objectToArray(value), Array.isArray(currentValue) ? currentValue : _objectToArray(currentValue), _protects, _ignores, fullPath)

          if (Object.keys(diff).length > 0 || isValueEmpty !== isCurrentEmpty) {
            result[key] = Object.keys(diff).length === 0 && Array.isArray(value) ? [] : diff
          }
        }
      }
    } else {
      // Key doesn't exist in current: add if it's not empty or if it's a scalar
      result[key] = _isArrayOrObject(value) ? _objectToArray(value) : value
    }
  }

  return result
}

/**
 * Check if value is scalar (primitive)
 */
function _isScalar(value) {
  return value === null || value === undefined || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value instanceof Date
}

/**
 * Check if value is array or object
 */
function _isArrayOrObject(value) {
  return Array.isArray(value) || (typeof value === 'object' && value !== null)
}

/**
 * Check if value is empty
 */
function _isEmpty(value) {
  if (value === null || value === undefined) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'string') return value.trim() === ''
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

/**
 * Weak equality comparison (like PHP ==)
 */
function _weakEqual(a, b) {
  if (a === b) return true
  if (a == b) return true // Type coercion

  // Convert to comparable values
  const normalizedA = _normalizeValue(a)
  const normalizedB = _normalizeValue(b)

  return normalizedA === normalizedB
}

/**
 * Convert object to array-like structure
 */
function _objectToArray(obj) {
  if (Array.isArray(obj)) return obj
  if (typeof obj === 'object' && obj !== null) {
    return Object.assign({}, obj)
  }
  return obj
}

/**
 * Normalize value for comparison
 */
function _normalizeValue(value) {
  if (value === null || value === undefined) {
    return value
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0
  }

  if (typeof value === 'string') {
    const num = Number(value)
    if (!isNaN(num) && isFinite(num)) {
      return num
    }
    return value
  }

  if (typeof value === 'number') {
    return value
  }

  if (value instanceof Date) {
    return value.getTime()
  }

  return value
}

export function mergeLeft(...args) {
  const isObject = (obj) => obj && typeof obj === 'object'
  const isTerminal = (obj) => obj instanceof Date || (globalThis.File && obj instanceof globalThis.File) || (globalThis.Blob && obj instanceof globalThis.Blob)

  let result = args[0]

  for (let i = 1; i < args.length; i++) {
    const s = args[i]
    if (!isObject(s)) continue

    if (isTerminal(s)) {
      result = s
      continue
    }

    if (!isObject(result)) {
      result = !Array.isArray(s) || (args[0] && !Array.isArray(args[0])) ? {} : []
    }

    Object.entries(s).forEach(([k, v]) => {
      if (isObject(v)) {
        result[k] = mergeLeft(result[k], v)
      } else {
        result[k] = v
      }
    })
  }
  return result
}
